import { useAppBridge } from '@shopify/app-bridge-react'
import { useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import useSWR from 'swr'

import { backendFetchResult, backendFetchResultData, Result } from './api'
import { anyWindow, useQuery } from './helpers'

export const setNpsSurveyScore = (score: number) =>
  backendFetchResult<Result<any>>('POST', '/shop/nps-survey-score', {
    body: JSON.stringify({ score }),
  })

export interface EligibleForReview {
  eligible: boolean
  needsNpsSurvey: boolean
}

const fetchEligibleForReview = () => {
  if (anyWindow.newUserTest || anyWindow.adminMode) {
    return Promise.resolve({ eligible: true, needsNpsSurvey: false })
  }

  return backendFetchResultData<EligibleForReview>('GET', '/shop/review-request-eligibility')
}

export const useEligibleForReviewSWR = () =>
  useSWR<EligibleForReview>('review-request-eligibility', fetchEligibleForReview, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  })

export const ShopifyReviewHandler = () => {
  const query = useQuery()
  const history = useHistory()
  const shopifyReview = useShopifyReview()

  useEffect(() => {
    if (query.get('review')) {
      shopifyReview.show(query.get('review') as string)
      query.delete('review')
      history.replace({ search: query.toString() })
    }
  }, [query, shopifyReview, history])

  return null
}

const addReviewRequest = (result: string, trigger: string) =>
  backendFetchResultData<void>('POST', '/shop/add-review-request', {
    body: JSON.stringify({ result, trigger }),
  })

export const useShopifyReview = () => {
  const shopify = useAppBridge()

  const show = useCallback(
    async (trigger: string) => {
      try {
        const result = await shopify.reviews.request()
        if (!result.success) {
          addReviewRequest(result.code, trigger)
        } else {
          addReviewRequest('success', trigger)
        }
      } catch (error) {
        addReviewRequest('error', trigger)
      }
    },
    [shopify]
  )

  const canSkipNpsSurvey =
    anyWindow.eligibleForReview && !anyWindow.eligibleForReview.needsNpsSurvey

  const showIfCanSkipNpsSurvey = useCallback(
    (trigger: string) => {
      if (canSkipNpsSurvey) {
        show(trigger)
      }
    },
    [show, canSkipNpsSurvey]
  )

  return { show, showIfCanSkipNpsSurvey, canSkipNpsSurvey }
}
